import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Button, Stack, Typography } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import { OrgCredentialType } from '@/types/credentials'
import { ProjectGridField } from '@/types/fields'
import { LiveListEntity } from '@/types/live-lists'
import { NewVendorBody } from '@/types/relay'
import {
  INVOICE_REMIT_ADDRESS_INFO_TYPE_ID,
  VENDOR_ADDRESS_INFO_TYPES_IDS,
  VENDOR_NAME_INFO_TYPES_IDS,
} from '@/utils/info-types'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { FormTextField, PixyDocsForm } from '@/components/forms'
import { useProjectGridFieldsContext } from '@/components/validation/ProjectGridFieldsProvider'
import useGetValueData from '@/components/validation/useGetValueData'

type NewVendorDialogProps = {
  credentialType: OrgCredentialType
  overlay: OverlayState
  onSave: (newVendorBody: NewVendorBody) => Promise<LiveListEntity>
}

type FormValues = {
  vendorName: string
  street1: string
  street2: string
  city: string
  state: string
  zip: string
  country: string
}

export default function NewVendorDialog({
  credentialType,
  overlay,
  onSave,
}: NewVendorDialogProps) {
  // Get the vendor name field, remit address field, vendor address field
  const fields = useProjectGridFieldsContext()

  // We don't render this component unless there is a vendor field,
  // so we don't need to check that here.
  const vendorNameField = fields.find((field) =>
    VENDOR_NAME_INFO_TYPES_IDS.includes(field.info_type_id as string),
  ) as ProjectGridField
  const remitAddressField = fields.find(
    (field) => field.info_type_id === INVOICE_REMIT_ADDRESS_INFO_TYPE_ID,
  ) // remit address only on invoices
  const vendorAddressField = fields.find((field) =>
    VENDOR_ADDRESS_INFO_TYPES_IDS.includes(field.info_type_id as string),
  ) as ProjectGridField

  const getValueData = useGetValueData()

  const { value: vendorNameValue } = getValueData({
    fieldId: vendorNameField.id,
  })

  const { jsonValue: remitAddressJson } = getValueData({
    fieldId: remitAddressField?.id,
  })
  const { jsonValue: vendorAddressJson } = getValueData({
    fieldId: vendorAddressField.id,
  })

  const defaultValues = useMemo<FormValues>(() => {
    const addressJson = remitAddressJson || vendorAddressJson
    const addressDetails = addressJson?.address || addressJson?.us_address
    const address = {
      street1: addressDetails?.street1 || '',
      street2: addressDetails?.street2 || '',
      city: addressDetails?.city || '',
      state: addressDetails?.administrative_area || addressDetails?.state || '',
      zip: addressDetails?.postal_code || addressDetails?.zip || '',
      country: addressDetails?.country || '',
    }
    return {
      vendorName: vendorNameValue as string,
      ...address,
    }
  }, [remitAddressJson, vendorAddressJson, vendorNameValue])

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
  })

  const {
    formState: { isValid },
    reset,
  } = methods

  function handleSubmit(values: FormValues) {
    const { vendorName, ...rest } = values
    onSave({
      name: vendorName,
      address: {
        ...rest,
      },
    }).catch(() => {})
    overlay.close()
  }

  const usesAddress = credentialType.supports.vendor_uses_address

  useEffect(() => {
    if (overlay.isOpen) {
      reset(defaultValues)
    }
  }, [defaultValues, overlay.isOpen, reset])

  return (
    <Dialog title="New Vendor" {...overlay} maxWidth="xs">
      <PixyDocsForm methods={methods} onSubmit={handleSubmit}>
        <DialogContent disablePadding>
          <Stack direction="column" spacing={3} sx={{ px: 3, pb: 3 }}>
            <Typography variant="body2">
              This will create a new vendor in {credentialType.name}. Any
              existing vendor information from the document will be entered into
              the form automatically.
            </Typography>

            <FormTextField
              name="vendorName"
              autoFocus
              fullWidth
              shrinkLabel
              label="Vendor Name"
              required
              requiredErrorText="Vendor Name is required"
              useControllerProps={{
                rules: {
                  validate: (value) => {
                    if (!value.trim().length) return 'Vendor Name is required'
                    return true
                  },
                },
              }}
            />

            {!!usesAddress && (
              <Stack direction="column" spacing={1}>
                <FormTextField
                  fullWidth
                  shrinkLabel
                  label="Street 1"
                  name="street1"
                />
                <FormTextField
                  fullWidth
                  shrinkLabel
                  label="Street 2"
                  name="street2"
                />
                <Stack direction="row" spacing={1}>
                  <FormTextField
                    fullWidth
                    shrinkLabel
                    label="City"
                    name="city"
                  />
                  <FormTextField
                    fullWidth
                    shrinkLabel
                    label="State"
                    name="state"
                  />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <FormTextField fullWidth shrinkLabel label="Zip" name="zip" />
                  <FormTextField
                    fullWidth
                    shrinkLabel
                    label="Country"
                    name="country"
                    // It might be that not all fields are required, so we will only use "required" for country
                    required={usesAddress === 'required'}
                  />
                </Stack>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Button type="submit" disabled={!isValid}>
            Save
          </Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
